import {
  hashKey,
  queryOptions,
  type Query,
  type QueryFunction,
  type QueryFunctionContext,
} from "@tanstack/react-query";
import { fetchPageData } from "./pageDataClient";
import { pageDataKeys } from "./pageDataKeys";
import { queryClient } from "~/app/queryClient";
import type { PageSlotData } from "./pageInitialDataTypes";

const GLOBAL_SLOTS_PATH = "/page-data/cms-structure?slots=global";
const ANONYMOUS_IDENTITY_STORAGE_KEY = "scha.pageData.globalSlots.anonymousIdentity";
const PERSISTED_CACHE_STORAGE_PREFIX = "scha.pageData.globalSlots.cache";
/**
 * Reloads reuse a persisted entry only briefly: aligned with the 60s staleTime story, one
 * maxAge window bounds how stale a served-from-storage shell may be before it must refetch.
 */
const PERSISTED_CACHE_MAX_AGE_MS = 5 * 60_000;

let anonymousFallbackIdentity: string | null = null;

export interface PageStructureData {
  uid: string;
  type: string;
  templateUid: string | null;
  title: string | null;
  slots: PageSlotData[];
}

/**
 * Anonymous sessions expose no SessionData.user.uid (the backend leaves anonymous user fields null),
 * so the global-slots key carries a per-tab-session identity instead. It is never shared across
 * visitors and is dropped together with the session by the logout sessionStorage clear.
 */
export function getAnonymousGlobalSlotsUserUid(): string {
  try {
    const existing = window.sessionStorage.getItem(ANONYMOUS_IDENTITY_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const created = createAnonymousIdentity();
    window.sessionStorage.setItem(ANONYMOUS_IDENTITY_STORAGE_KEY, created);
    return created;
  } catch {
    // sessionStorage can be unavailable (private mode); a per-SPA-instance fallback still
    // keeps anonymous visitors from sharing one cache entry.
    anonymousFallbackIdentity ??= createAnonymousIdentity();
    return anonymousFallbackIdentity;
  }
}

function createAnonymousIdentity(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

interface PersistedGlobalSlotsEntry {
  data: PageStructureData;
  dataUpdatedAt: number;
}

function persistedCacheStorageKey(queryKey: readonly unknown[]): string {
  return `${PERSISTED_CACHE_STORAGE_PREFIX}.${hashKey(queryKey)}`;
}

/** Reads a fresh (≤ maxAge) persisted entry; expired, corrupt, or unavailable storage is a miss. */
function readPersistedGlobalSlotsEntry(storageKey: string): PersistedGlobalSlotsEntry | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const entry = JSON.parse(raw) as PersistedGlobalSlotsEntry;
    if (typeof entry?.dataUpdatedAt !== "number" || !Array.isArray(entry.data?.slots)) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }
    if (Date.now() - entry.dataUpdatedAt > PERSISTED_CACHE_MAX_AGE_MS) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

function writePersistedGlobalSlotsEntry(storageKey: string, data: PageStructureData): void {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify({ data, dataUpdatedAt: Date.now() }));
  } catch {
    // Persistence is best-effort; the in-memory query cache still serves this SPA instance.
  }
}

function removePersistedGlobalSlotsEntries(): void {
  try {
    const cacheKeys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(PERSISTED_CACHE_STORAGE_PREFIX)) {
        cacheKeys.push(key);
      }
    }
    cacheKeys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // sessionStorage unavailable — nothing persisted to remove.
  }
}

/**
 * TanStack per-query persister with the same contract as @tanstack/query-persist-client-core's
 * experimental_createQueryPersister, hand-rolled for this single query: entries live under a
 * legible sessionStorage prefix (hashed query key suffix) so the logout/account-switch seams can
 * remove them by prefix without iterating an entries() view plain sessionStorage does not offer.
 *
 * Only a fresh in-memory miss consults storage. Restoring re-ages the query to the persisted
 * dataUpdatedAt (after the surrounding fetch settles — a synchronous write would be clobbered by
 * the success dispatch), so staleTime governs the background refetch exactly as after a network
 * fetch.
 */
const persistGlobalSlotsToSessionStorage = async <TQueryKey extends readonly unknown[]>(
  queryFn: QueryFunction<PageStructureData, TQueryKey>,
  context: QueryFunctionContext<TQueryKey>,
  query: Query,
): Promise<PageStructureData> => {
  const storageKey = persistedCacheStorageKey(query.queryKey);
  const persisted = query.state.data === undefined ? readPersistedGlobalSlotsEntry(storageKey) : null;
  if (persisted) {
    setTimeout(() => {
      query.setState({ dataUpdatedAt: persisted.dataUpdatedAt });
      if (query.isStale()) {
        void query.fetch();
      }
    }, 0);
    return persisted.data;
  }
  const data = await queryFn(context);
  writePersistedGlobalSlotsEntry(storageKey, data);
  return data;
};

export const globalSlotsQueryOptions = (userUid: string, accountKey: string, isPreview: boolean) =>
  queryOptions({
    queryKey: isPreview
      ? ([...pageDataKeys.globalSlots(userUid, accountKey), "preview"] as const)
      : pageDataKeys.globalSlots(userUid, accountKey),
    queryFn: () => fetchPageData<PageStructureData>(GLOBAL_SLOTS_PATH),
    // Preview must never persist: staged SmartEdit slots may not survive even a reload,
    // mirroring the gcTime: 0 no-cache contract below.
    persister: isPreview ? undefined : persistGlobalSlotsToSessionStorage,
    staleTime: isPreview ? 0 : 60_000,
    // Preview entries must not survive remounts (stale-while-revalidate would flash staged
    // SmartEdit slots), mirroring the house preview query's gcTime: 0.
    gcTime: isPreview ? 0 : undefined,
    refetchOnWindowFocus: false,
    retry: 1,
  });

/**
 * Drops every cached global-slots entry (all identities, including preview) without refetching:
 * the in-memory query cache AND the sessionStorage-persisted entries, so logout/session expiry
 * and account-context switches never restore another identity's shell slots.
 */
export function removeGlobalSlotsCache(): void {
  void queryClient.removeQueries({ queryKey: [...pageDataKeys.all, "global-slots"] });
  removePersistedGlobalSlotsEntries();
}
